import router from '@/router';
import userApi from '@/services/userApi';

import { objNotEmpty } from '@/utils';

/**
 * @description Process the user's credentials and query the User API to authenticate
 * @param {object: { initials: string, email: string, password: string }} credentials
 * @throws an error, if the User API response is not OK
 */
export const login = async ({ dispatch }, credentials) => {
  const { initials, agencyId, password } = credentials;

  const username = `${initials}@${agencyId}`;

  const response = await userApi.login({ username, password });

  if (!response.ok) throw response;

  await dispatch('setUserResources', response.data);

  await dispatch('getAuthContext');
};

/**
 * @description Set user profile data from claims
 * @param {object} claims
 */
export const setUserProfile = ({ commit }, claims) => {
  commit('purgeUserProfile');
  commit('setUserProfile', claims);
};

/**
 * @description Signout the user and clear the current access token renewal task
 */
export const logout = async ({ commit, dispatch, state }) => {
  // only call if user hasn't passed through here yet
  if (state.isAuthenticated) await userApi.logout();

  if (router.currentRoute.path !== '/signin') {
    router.push({ path: '/signin' });
  }

  localStorage.clear();
  await dispatch('resetClientState', null, { root: true });
  await dispatch('search/resetSearch', null, { root: true });

  commit('clearRenewalTask');
  commit('resetState');
};

/**
 * @description Renew the user's access token
 */
export const renewToken = async ({ dispatch }) => {
  const { ok, data } = await userApi.renewAuth();
  if (!ok || !objNotEmpty(data)) {
    await dispatch('logout');
    return;
  }

  await dispatch('setUserResources', data);
  await dispatch('getAuthContext');
};

/**
 * @description Schedule auth token renewal task,
 * EITHER immediately => e.g. user is returning to site, has a Cookie that may or may not be valid
 * OR 15 minutes prior to that access token's expiry => user has been on site for at least (expiry - 15min)
 */
export const autoRenew = async ({ state, commit, dispatch }) => {
  // refreshed site, or returning to site after some period of time; attempt to renew
  if (!state.isAuthenticated) {
    await dispatch('renewToken');
    return;
  }

  const exp = state.exp;
  // conv `now` to seconds because exp claim is represented as seconds since UNIX epoch
  const now = Date.now() / 1000;
  // diff between expiration in seconds since epoch and current time in seconds since epoch
  let timeUntilRenewal = exp - now;
  // adjust renewal time to 15 min prior to expiration
  timeUntilRenewal -= (15 * 60);
  // set timeout to renewal time in ms
  const renewalTask = setTimeout(() => dispatch('renewToken'), timeUntilRenewal * 1000);

  commit('trackRenewalTask', renewalTask);
};

/**
 * @description Determine the access token's state (invalid, expired, ok)
 */
export const getAuthContext = async ({ dispatch }) => {
  const { ok, data } = await userApi.whoAmI();

  if (ok && data.claims) {
    await dispatch('setUserProfile', data.claims)
      .then(dispatch.bind(null, 'autoRenew'));
    return;
  }

  await dispatch('logout');
};

/**
 * @description Set user resources e.g. product urls
 */
export const setUserResources = ({ commit }, data) => {
  commit('setUserResources', data);
};
